import LivePreviewBase from "./live_preview_base"
import {
  applyTextStrokeToElements,
  applyFontSizeScale,
  applyTypographyToElements,
  applyTextShadowToElements,
} from "../utils/text_treatment"
import { TYPE_PAIR_SPECS } from "../services/language_learning_type_pairs"

// Live-preview controller for the Backchain Builder wizard.
//
// The preview mirrors FRAME 0 — the header, the hook, and the FULL sentence
// stacked one chunk per line are all painted, with the bottom line already
// lit. So every applier here repaints an element that is already on screen;
// nothing fades anything in. In particular the ghost lines are a dimmed
// COLOUR, never a reduced opacity, so there is no "reveal" for this
// controller to drive — the theme applier repaints the ghost mix instead.
//
// Chrome fields (hook, why, CTA, language) update live; per-row chunk editing
// is visible after a save round-trip, like Pattern Unlock's pairs. The step
// pill is digits only (`1/3`) so it needs no locale template — it is derived
// from the saved chunk count server-side.
//
// TYPE_PAIR_SPECS is imported from the shared language-learning catalog
// (de-mirrored — the font data is authored once in the TS source and consumed
// by both legs), so the preview can never drift from what the renderer paints.
//
// Mounted alongside `palette-live-preview`, which owns the background layers +
// tint/darkness sliders. Inherits event wiring + dispatch + value
// normalisation from LivePreviewBase.
export default class extends LivePreviewBase {
  static targets = [
    "frame", "flag", "languageLabel", "stepPill", "backwardsBadge",
    "hookText", "chunkLine", "contourEcho",
    "whyText", "ctaText",
  ]

  static FIELD_BINDINGS = {
    hook_text:            { apply: "applyHookText" },
    why_text:             { apply: "applyWhyText" },
    cta_text:             { apply: "applyCtaText" },
    target_language_code: { apply: "applyTargetLanguage" },
    theme:                { apply: "applyTheme" },
    type_pair:            { apply: "applyTypography" },
    italic_override:      { apply: "applyTypography" },
    font_size_scale:      { apply: "applyFontSize" },
    display_override:     { apply: "applyFontSize" },
    body_override:        { apply: "applyFontSize" },
    text_shadow:          { apply: "applyTextShadow" },
    text_stroke_color:    { apply: "applyStroke" },
    text_stroke_width:    { apply: "applyStroke" },
  }

  // -- chrome ---------------------------------------------------------------

  // The hook is drawn the way the renderer draws it: language tags are an
  // authoring instruction for the take splitter, never ink.
  applyHookText() {
    this.setText("hookText", stripLanguageTags(this.fieldValue("hook_text")) || "Your hook goes here.")
  }

  applyWhyText() { this.setText("whyText", this.fieldValue("why_text") || "Why this works goes here.") }
  applyCtaText() { this.setText("ctaText", this.fieldValue("cta_text")) }

  applyTargetLanguage({ value }) {
    this.setText("languageLabel", LANGUAGE_NAMES[value] || (value || "").toUpperCase())
    if (this.hasFlagTarget) this.flagTarget.textContent = FLAGS[value] || "🏳️"
  }

  // The palette drives BOTH inks: the lit lines take `--p-text` straight, and
  // the ghost lines take a mix of it toward `--p-bg` (the CSS twin of the
  // renderer's mixHex(text, background, 0.7)). Swapping the custom properties
  // repaints both without touching a single opacity.
  applyTheme({ value }) {
    if (!this.hasFrameTarget) return
    const palette = THEME_PALETTES[value] || THEME_PALETTES.notebook
    const f = this.frameTarget
    f.style.setProperty("--p-bg", palette.bg)
    f.style.setProperty("--p-surface", palette.surface)
    f.style.setProperty("--p-text", palette.text)
    f.style.setProperty("--p-muted", palette.muted)
    f.style.setProperty("--p-accent", palette.accent)
  }

  // -- typography / font-size / stroke --------------------------------------

  applyTypography() {
    const spec = TYPE_PAIR_SPECS[this.fieldValue("type_pair") || "default"]
    if (!spec) return
    const display = []
    const body = []
    if (this.hasHookTextTarget) display.push(this.hookTextTarget)
    if (this.hasWhyTextTarget) display.push(this.whyTextTarget)
    if (this.hasCtaTextTarget) body.push(this.ctaTextTarget)
    if (this.hasContourEchoTarget) body.push(this.contourEchoTarget)
    this.chunkLineTargets.forEach((el) => body.push(el))
    applyTypographyToElements({ display, body }, spec, this.fieldValue("italic_override") || "auto")
  }

  applyFontSize() {
    const globalScale = positiveOr(this.fieldValue("font_size_scale"), 1)
    const displayScale = this.readPositiveOverride("display_override") ?? globalScale
    const bodyScale    = this.readPositiveOverride("body_override")    ?? globalScale

    const displayPairs = []
    if (this.hasHookTextTarget) displayPairs.push(sizedPair(this.hookTextTarget, 13))
    if (this.hasWhyTextTarget) displayPairs.push(sizedPair(this.whyTextTarget, 14))
    applyFontSizeScale(displayPairs, displayScale)

    const bodyPairs = []
    if (this.hasCtaTextTarget) bodyPairs.push(sizedPair(this.ctaTextTarget, 10))
    for (const el of this.chunkLineTargets) bodyPairs.push(sizedPair(el, 17))
    applyFontSizeScale(bodyPairs, bodyScale)
  }

  // Stroke rides the opted-in elements only — the hook + the why line. Stack
  // rows sit on the theme surface and skip, mirroring the renderer's
  // applyStroke: false on the stack/pill roles.
  applyStroke() {
    const targets = Array.from(
      this.element.querySelectorAll("[data-stroke-target]"),
    ).map((el) => ({
      element: el,
      basePx: parseFloat(el.dataset.strokeBasePx) || undefined,
      skip: el.dataset.strokeSkip === "true",
    }))
    if (!targets.length) return
    applyTextStrokeToElements(targets, {
      width: this.fieldValue("text_stroke_width"),
      color: this.fieldValue("text_stroke_color") || "#000000",
    })
  }

  applyTextShadow({ value }) {
    applyTextShadowToElements(
      Array.from(this.element.querySelectorAll("[data-stroke-target]")),
      value,
    )
  }

  readPositiveOverride(fieldName) {
    for (const el of this.fieldElements(fieldName)) {
      if (el.type === "range" && !el.disabled) {
        const v = parseFloat(el.value)
        return Number.isFinite(v) && v > 0 ? v : null
      }
    }
    return null
  }

  // -- preview DOM helpers --------------------------------------------------

  setText(targetName, value) {
    const has = `has${targetName[0].toUpperCase() + targetName.slice(1)}Target`
    if (this[has]) this[`${targetName}Target`].textContent = value
  }
}

// Every scaled element carries its renderer-relative base size in
// `data-base-font-size` (server-rendered), so the JS never restates the
// preview's size table — the fallback is only for a hand-built DOM.
function sizedPair(element, fallbackBasePx) {
  return { element, basePx: parseFloat(element.dataset.baseFontSize) || fallbackBasePx }
}

function positiveOr(value, fallback) {
  if (value === null || value === undefined) return fallback
  const n = typeof value === "number" ? value : parseFloat(value)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

// Authoring language spans (<en>…</en>, <es>…</es>) split the spoken take;
// they are never drawn. Mirrors LanguageRuns.strip_tags on the Ruby side.
function stripLanguageTags(text) {
  return (text || "").replace(/<\/?[a-zA-Z][a-zA-Z-]*>/g, "").replace(/\s+/g, " ").trim()
}

const THEME_PALETTES = {
  "notebook":   { "bg": "#FAF7EC", "surface": "#FFFFFF", "text": "#1F2937", "muted": "#6B7280", "accent": "#E8A838" },
  "chalkboard": { "bg": "#1F2A24", "surface": "#243530", "text": "#F4F3EC", "muted": "#C5C2AE", "accent": "#FFE066" },
  "whiteboard": { "bg": "#F8FAFC", "surface": "#FFFFFF", "text": "#111827", "muted": "#4B5563", "accent": "#DC2626" },
  "cork_board": { "bg": "#C08B5C", "surface": "#FEF3C7", "text": "#1C1917", "muted": "#57534E", "accent": "#EAB308" },
  "minimal":    { "bg": "#FFFFFF", "surface": "#F8FAFC", "text": "#0F172A", "muted": "#64748B", "accent": "#6366F1" },
}

const LANGUAGE_NAMES = {
  "ar": "Arabic", "de": "German", "en": "English", "es": "Spanish", "fr": "French",
  "hi": "Hindi", "it": "Italian", "ja": "Japanese", "ko": "Korean", "nl": "Dutch",
  "pl": "Polish", "pt": "Portuguese", "ru": "Russian", "sv": "Swedish", "th": "Thai",
  "tr": "Turkish", "vi": "Vietnamese", "zh": "Chinese",
}

const FLAGS = {
  "ar": "🇸🇦", "de": "🇩🇪", "en": "🇬🇧", "es": "🇪🇸", "fr": "🇫🇷",
  "hi": "🇮🇳", "it": "🇮🇹", "ja": "🇯🇵", "ko": "🇰🇷", "nl": "🇳🇱",
  "pl": "🇵🇱", "pt": "🇵🇹", "ru": "🇷🇺", "sv": "🇸🇪", "th": "🇹🇭",
  "tr": "🇹🇷", "vi": "🇻🇳", "zh": "🇨🇳",
}
